<template>
  <div class="flex flex-col space-y-2">
    <div class="flex justify-end py-2">
      <DButton v-if="isProjectorExist" @click="exportCsv" size="small" severity="primary" :disabled="!isProjectorExist">
        <Download size="14" />
        Export CSV
      </DButton>
    </div>
    <div v-if="isProjectorExist && rows.length > 0">
      <DDataTable :value="rows">
        <Column v-for="(col, index) of Object.keys(rows[0])" :key="col" :header="getHeader(col)" :field="col"></Column>
      </DDataTable>
    </div>
    <div v-else>
      <div class="mb-5 flex w-full items-center justify-center text-lg font-semibold text-gray-500">
        No projectors found to export.
      </div>
    </div>
  </div>
</template>

<script setup>
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { Column, DButton, DDataTable } from '@disguise/cloud-ui';
import { Download } from 'lucide-vue-next';
import Papa from 'papaparse';
import * as THREE from 'three';
import { computed, onMounted, onUnmounted, ref } from 'vue';
const cruxModuleStore = useCruxModuleStore();

const rows = ref([]);

const isProjectorExist = computed(() => {
  return cruxModuleStore.cruxModules.some((module) => module.type === 'projector');
});

const fixedDecimal = (num, decimal = 2) => {
  return Number.parseFloat(num).toFixed(decimal);
};

const buildRow = () => {
  cruxModuleStore.cruxModules.forEach((row) => {
    if (row.type === 'projector') {
      const data = row?.data ?? {};

      const totalLumens = data.lumens * (data.power_brightness / 100) * (data.stackedProjectors ?? 1);

      rows.value.push({
        Projector_Name: row?.name ?? null,
        'Projector_Qte(Stack)': data?.stackedProjectors ?? 1,
        'Projector_Native-Rez-X': null,
        'Projector_Native-Rez-Y': null,
        'Projector_Lumens(lux)': data?.lumens ?? null,
        'Projector_Brightness(%)': data?.power_brightness ?? null,
        'Projector_Total_Lumens(lux)': totalLumens,
        'Projector_Trow-Ratio': data?.throwRatio ?? null,
        'Lens_Shift-H(%)': data?.lensShiftHorizontal ?? null,
        'Lens_Shift-V(%)': data?.lensShiftVertical ?? null,
        Lens_X: data?.position?.x ?? null,
        Lens_Y: data?.position?.y ?? null,
        Lens_Z: data?.position?.z ?? null,
        'Pitch(deg)': fixedDecimal(THREE.MathUtils.radToDeg(data?.rotation?.x ?? null)),
        'Yaw(deg)': fixedDecimal(THREE.MathUtils.radToDeg(data?.rotation?.y ?? null)),
        'Roll(deg)': fixedDecimal(THREE.MathUtils.radToDeg(data?.rotation?.z ?? null)),
        Target_X: data?.target?.x ?? null,
        Target_Y: data?.target?.y ?? null,
        Target_Z: data?.target?.z ?? null,
        Target_Distance: data?.throwDistance ?? null,
        Target_Width: null,
        Target_Height: null,
        Target_Illuminance: null,
        Target_DPI: null,
        Unit_Dim: null,
        Unit_Illuminance: null,
        Projector_code: row?.code ?? null,
      });
    }
  });
};

onMounted(() => {
  buildRow();
});

const exportCsv = () => {
  const csv = Papa.unparse(rows.value);

  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;',
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'projectors.csv';
  link.click();
};

const getHeader = (col) => {
  return col
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

onUnmounted(() => {
  rows.value = [];
});
</script>
